import { SupabaseClient, createClient } from "@supabase/supabase-js";

import { Database } from "../supabase.types";
import fetchRetry from "fetch-retry";

// We create **one** unauthenticated Supabase client and reuse it across the
// entire web bundle.  Creating a client is cheap on desktop but on mobile it
// allocates event-emitters, WebSocket plumbing, etc.  Doing that once per
// component mount (e.g. inside a `useEffect`) easily floods the main thread
// and causes scroll jankiness.
let _noAuthClient: SupabaseClient<Database> | null = null;

export default function createNoAuthSupabaseClient() {
  if (_noAuthClient) return _noAuthClient;

  _noAuthClient = createClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL as string,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY as string,
    {
      auth: {
        persistSession: false,
      },
    }
  );

  return _noAuthClient;
}

// NOTE: This is experimental; do not widely use in production yet. -SL
export function createNoAuthSupabaseClientWithRetry() {
  const fetchWithRetry = fetchRetry(fetch, {
    retries: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000), // Exponential backoff
  });

  return createClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL as string,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY as string,
    {
      auth: {
        persistSession: false,
      },
      global: {
        fetch: fetchWithRetry,
      },
    }
  );
}

export async function getSession(supabase: SupabaseClient<Database>) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session;
}
/**
 * Helper function to get the current user from the supabase client.
 *
 * NOTE for server side usage:
 * This is recommended over getting the user from the session due to how mobile
 * clients authenticate. When users on the mobile app make requests to the API,
 * the authorization is not persistent, meaning the session will be null. The
 * user object is only stored on supabase.auth.getUser() in this scenario. This
 * is why for reusable code that might be used in the API layer, it’s recommended
 * to avoid using `supabase.auth.getSession()` and use `getUser()` instead.
 * See https://github.com/PunchupLive/downstroke/pull/774
 * Supabase docs: https://supabase.com/docs/reference/javascript/auth-getuser
 */
export async function getUser(supabase: SupabaseClient<Database>) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
}

export function createSupabaseAdminClient() {
  return createClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL ?? "",
    process.env.SUPABASE_SERVICE_ROLE_KEY ?? "",
    {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
      },
    }
  );
}

export function createSupabaseAdminClientWithRetry() {
  const fetchWithRetry = fetchRetry(fetch, {
    retries: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
  });

  return createClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL ?? "",
    process.env.SUPABASE_SERVICE_ROLE_KEY ?? "",
    {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
      },
      global: {
        fetch: fetchWithRetry,
      },
    }
  );
}

// Experimental, do not adopt widely yet
export function createSupabaseReadOnlyReplicaAdminClient() {
  // In non-production environments, default to the regular Supabase URL
  const replicaUrl =
    process.env.VERCEL_ENV === "production"
      ? process.env.NEXT_PUBLIC_SUPABASE_REPLICA_URL
      : process.env.NEXT_PUBLIC_SUPABASE_URL;

  return createClient<Database>(
    replicaUrl ?? "",
    process.env.SUPABASE_SERVICE_ROLE_KEY ?? "",
    {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
      },
    }
  );
}
